/**
 * 🔧 FEATURE SERVICE - NEW 24 MODULE SYSTEM
 * 
 * Provides feature gating functionality based on the new modular license system.
 * Supports: ALL, Individual Modules, and CUSTOM module selection
 */

import i18n from '../../i18n';
import licensingService from './licensingService';
import {
  MODULES,
  MODULE_ROUTES,
  MODULE_NAMES,
  resolveModuleDisplayName,
  getEnabledModules,
  hasAccessToRoute,
  hasAccessToModule,
  getModuleFromRoute
} from '../config/licenseFeatures';
import { moduleStructure } from '../config/moduleStructure';

const moduleLabel = (moduleKey) => resolveModuleDisplayName(moduleKey, i18n.t.bind(i18n));

const fs = (key, opts) => i18n.t(`serviceMessages.feature.${key}`, opts);
const cm = (key, opts) => i18n.t(`serviceMessages.common.${key}`, opts);

class FeatureService {
  constructor() {
    this.cachedLicense = null;
    this.cachedEnabledModules = null;
  }

  /**
   * 🔑 Get current license from licensing service
   */
  getCurrentLicense() {
    // Use cached value if available
    if (this.cachedLicense) {
      return this.cachedLicense;
    }

    // Get from licensing service
    const license = licensingService.getLicense();
    
    if (!license) {
      console.warn('⚠️ No license found!');
      return null;
    }
    
    // Cache the result
    this.cachedLicense = license;
    
    console.log('🔑 License loaded:', {
      appType: license.appType,
      keyType: license.keyType,
      clientName: license.clientName,
      valid: license.valid
    });
    
    return license;
  }

  /**
   * 🔑 Get current license type (for compatibility)
   */
  getCurrentLicenseType() {
    const license = this.getCurrentLicense();
    return license ? license.appType : null;
  }

  /**
   * 📋 Get all enabled modules for current license
   * @returns {Array} - Array of enabled module keys
   */
  getMyEnabledModules() {
    // Use cached value if available
    if (this.cachedEnabledModules) {
      return this.cachedEnabledModules;
    }

    const license = this.getCurrentLicense();
    
    if (!license) {
      console.warn('⚠️ No license - no modules enabled');
      return [];
    }
    
    const enabledModules = getEnabledModules(license);
    
    // Cache the result
    this.cachedEnabledModules = enabledModules;
    
    console.log('📦 Enabled modules:', enabledModules);
    
    return enabledModules;
  }

  /**
   * ✅ Check if a specific feature/route is enabled for the current license
   * @param {string} route - The route (e.g., '/dashboard' or '/clients')
   * @returns {boolean} - True if feature is enabled, false otherwise
   */
  isFeatureEnabled(route) {
    // Settings is always available
    if (route === '/settings' || route.startsWith('/settings/')) {
      return true;
    }

    const license = this.getCurrentLicense();
    
    if (!license) {
      console.warn('⚠️ No license - feature disabled:', route);
      return false;
    }

    const hasAccess = hasAccessToRoute(license, route);
    
    if (!hasAccess) {
      console.log('🔒 Access denied to route:', route);
    }
    
    return hasAccess;
  }

  /**
   * 🔒 Check if user has access to a specific module
   * @param {string} moduleKey - The module key (e.g., 'FACTURATION')
   * @returns {boolean} - True if module is enabled
   */
  hasModuleAccess(moduleKey) {
    const license = this.getCurrentLicense();
    
    if (!license) {
      return false;
    }
    
    return hasAccessToModule(license, moduleKey);
  }

  /**
   * 🔍 Get module key from a route
   * @param {string} route - The route
   * @returns {string|null} - Module key or null
   */
  getModuleForRoute(route) {
    return getModuleFromRoute(route);
  }

  /**
   * 📊 Get current license display name
   * @returns {string} - Friendly license name
   */
  getCurrentLicenseName() {
    const license = this.getCurrentLicense();
    
    if (!license) {
      return fs('no_license');
    }
    
    const appType = license.appType;
    
    if (appType === 'ALL' || appType === 'ALL_MODULES') {
      return fs('complete');
    }
    
    if (appType && appType.startsWith('CUSTOM:')) {
      const enabledModules = this.getMyEnabledModules();
      return fs('custom_config', { count: enabledModules.length });
  }

    // Single module
    if (MODULE_NAMES[appType]) {
      return `Qawaem ${moduleLabel(appType)}`;
    }
    
    // Legacy support
    if (appType === 'ENTERPRISE') {
      return fs('enterprise_legacy');
    }
    
    if (appType === 'PROFESSIONNEL') {
      return fs('professional_legacy');
    }
    
    return appType || fs('unknown_license');
  }

  /**
   * 🔢 Get count of enabled modules
   * @returns {number} - Number of enabled modules
   */
  getEnabledModuleCount() {
    const enabledModules = this.getMyEnabledModules();
    return enabledModules.length;
  }

  /**
   * Sidebar nav modules the current license can access (moduleStructure order).
   */
  getLicensedNavModules() {
    return moduleStructure.filter(
      (module) => this.hasModuleAccess(module.key) && module.subsections?.length > 0
    );
  }

  /**
   * First landing route for this license (first subsection of first licensed module).
   */
  getDefaultLandingRoute() {
    const modules = this.getLicensedNavModules();
    const href = modules[0]?.subsections?.[0]?.href;
    return href || '/document-creation';
  }

  /**
   * Licensed module that owns the route, if any.
   */
  getLicensedModuleForPath(pathname) {
    return this.getLicensedNavModules().find((module) =>
      module.subsections.some(
        (subsection) =>
          pathname === subsection.href || pathname.startsWith(subsection.href + '/')
      )
    );
  }

  /**
   * 🔄 Clear cache (call after license change)
   */
  clearCache() {
    this.cachedLicense = null;
    this.cachedEnabledModules = null;
    console.log('🔄 Feature service cache cleared');
  }

  /**
   * 🔒 Check if user needs to upgrade to access a feature
   * @param {string} route - The route
   * @returns {Object} - { hasAccess: boolean, requiredModule: string }
   */
  checkFeatureAccess(route) {
    const hasAccess = this.isFeatureEnabled(route);
    const requiredModule = getModuleFromRoute(route);
    const currentLicense = this.getCurrentLicense();
    
    return {
      hasAccess: hasAccess,
      needsUpgrade: !hasAccess,
      requiredModule: requiredModule,
      requiredModuleName: requiredModule ? moduleLabel(requiredModule) : cm('unknown'),
      currentLicenseName: this.getCurrentLicenseName(),
      currentModuleCount: this.getEnabledModuleCount()
    };
  }

  /**
   * 🎯 Get all available modules
   * @returns {Object} - MODULES object
   */
  getAllModules() {
    return MODULES;
  }

  /**
   * 🔍 Filter menu items based on license
   * @param {Array} menuItems - Array of menu item objects with 'href' or 'path' property
   * @returns {Array} - Filtered menu items
   */
  filterMenuItems(menuItems) {
    if (!menuItems || !Array.isArray(menuItems)) {
      return [];
    }

    return menuItems.filter(item => {
      // If item has no route, keep it (might be a header/separator)
      if (!item.href && !item.path && !item.route) {
    return true;
  }
  
      const route = item.href || item.path || item.route;
      return this.isFeatureEnabled(route);
    });
  }

  /**
   * 🔍 Filter modules based on license
   * @param {Array} modules - Array of module objects with 'key' property
   * @returns {Array} - Filtered modules
   */
  filterModules(modules) {
    if (!modules || !Array.isArray(modules)) {
      return [];
    }

    const enabledModules = this.getMyEnabledModules();
    
    return modules.filter(module => {
      return enabledModules.includes(module.key);
    });
  }

  /**
   * 📈 Check if license is valid
   * @returns {boolean} - True if license is valid
   */
  isLicenseValid() {
    const license = this.getCurrentLicense();
    
    if (!license) {
      return false;
    }
    
    // Check if license has valid property
    if (license.valid === false) {
      return false;
    }
    
    // Check expiry date if present
    if (license.expiryDate) {
      const expiryDate = new Date(license.expiryDate);
      const now = new Date();
      
      if (expiryDate < now) {
        console.warn('⚠️ License expired:', license.expiryDate);
        return false;
      }
    }
    
    return true;
  }

  /**
   * 🆔 Get license client name
   * @returns {string} - Client name or 'Unknown'
   */
  getClientName() {
    const license = this.getCurrentLicense();
    return license ? license.clientName || cm('unknown') : fs('no_license');
  }

  /**
   * 📅 Get license expiry date
   * @returns {string|null} - Expiry date or null
   */
  getExpiryDate() {
    const license = this.getCurrentLicense();
    return license ? license.expiryDate : null;
  }

  /**
   * 🔑 Get license key (masked for security)
   * @returns {string} - Masked license key
   */
  getMaskedLicenseKey() {
    const license = this.getCurrentLicense();
    
    if (!license || !license.key) {
      return fs('no_license_key');
    }
    
    const key = license.key;
    
    // Show first 4 and last 4 characters
    if (key.length > 12) {
      return `${key.substring(0, 4)}...${key.substring(key.length - 4)}`;
    }
    
    return key;
  }

  /**
   * 📝 Get metadata for a feature/route
   * @param {string} route - The route (e.g., '/dashboard' or '/clients')
   * @returns {object} - Feature metadata {name, module, enabled}
   */
  getFeatureMetadata(route) {
    const module = getModuleFromRoute(route);
    const enabled = this.isFeatureEnabled(route);
    
    return {
      name: route,
      module: module || cm('unknown'),
      moduleName: module ? moduleLabel(module) : fs('unknown_module'),
      enabled: enabled
    };
  }
}

// Export singleton instance
const featureService = new FeatureService();

export default featureService;

// Named exports for specific functions
export const isFeatureEnabled = (route) => featureService.isFeatureEnabled(route);
export const hasModuleAccess = (moduleKey) => featureService.hasModuleAccess(moduleKey);
export const getMyEnabledModules = () => featureService.getMyEnabledModules();
export const getEnabledModuleCount = () => featureService.getEnabledModuleCount();
export const getCurrentLicenseName = () => featureService.getCurrentLicenseName();
export const checkFeatureAccess = (route) => featureService.checkFeatureAccess(route);
export const clearFeatureCache = () => featureService.clearCache();
export const isLicenseValid = () => featureService.isLicenseValid();

/**
 * 🏷️ Get app name based on current license type
 * @returns {string} - App name with license tier
 */
export const getAppName = () => {
  return featureService.getCurrentLicenseName();
};

/**
 * 🎯 Get current license type (for compatibility with old code)
 * @returns {string} - License type
 */
export const getBuildTarget = () => {
  return featureService.getCurrentLicenseType() || 'FACTURATION';
};

/**
 * 🔑 Get license key (masked)
 * @returns {string} - Masked license key
 */
export const getLicenseKey = () => {
  return featureService.getMaskedLicenseKey();
};

